import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { Challenge } from '@/model';
import { useNavigate } from 'react-router';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import {
  useDeleteChallengeMutation,
  useGetChallenge,
  useGetChallengeMetricsMutation,
  usePutChallengeCloseMutation,
} from '@/services/queries/challenge';
import { ChallengeFilters, ChallengeInteractivityFilter } from './types';
import { ChallengeFiltersBox } from './components/ChallengeFiltersBox';
import { ChallengeTable } from './components/ChallengeTable';
import dayjs from '@/utils/dayjs.config';
import { useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import { NumberChartCard } from '@/components/NumberChartCard';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationChallenge = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirmModalClose, setIsConfirmModalClose] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Challenge | undefined>(undefined);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.CHALLENGE);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();

  const { data: metrics, isLoading: isLoadingMetrics } = useGetChallengeMetricsMutation();

  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);
  const closeMutation = usePutChallengeCloseMutation();

  // regra condicional para filtro local de interação que não existe no modelo de dados
  const prepareChallengeFilters = (filters: ChallengeFilters): ChallengeFilters => {
    return {
      title: filters.title ?? '',
      status: filters.status,
      interactivity: filters.interactivity !== ChallengeInteractivityFilter.NOT_STARTED ? filters.interactivity : undefined,
      start_date: filters.start_date ?? '',
      end_date: filters.end_date ?? '',
      ...(filters.interactivity === ChallengeInteractivityFilter.NOT_STARTED && {
        starts_in: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
      }),
    };
  };

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_challenge.application_challenge_enable_success_toast')
      : t('pages.application_challenge.application_challenge_disable_success_toast');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_challenge.application_challenge_enable_error_toast')
      : t('pages.application_challenge.application_challenge_disable_error_toast');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.CHALLENGE, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const deleteMutation = useDeleteChallengeMutation();

  const filtersForm = useFiltersForm<ChallengeFilters>({
    defaultValues: {
      team_uuid: '',
      title: '',
      start_date: '',
      end_date: '',
      status: undefined,
      interactivity: undefined,
    },
  });
  const watchedFilters = useWatch({ control: filtersForm.control });

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const preparedFilters = useMemo(() => {
    return prepareChallengeFilters(watchedFilters);
  }, [watchedFilters]);

  const params = useMemo(() => {
    return GetParamsObject(
      preparedFilters,
      pagination.pageNumber,
      pagination.pageSize,
      sorting.desc ? 'desc' : 'asc',
      sorting.field,
    );
  }, [preparedFilters, pagination.pageNumber, pagination.pageSize, sorting]);

  const { data: itemData, isLoading, forceRefetch } = useGetChallenge(params);

  const itemsData = itemData?.data || [];

  const { data: clubs = [] } = useAllClub(true);

  const onHandleDelete = (item: Challenge) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleClose = (item: Challenge) => {
    setSelectedItem(item);
    setIsConfirmModalClose(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_challenge.application_challenge_delete_success_toast'),
          status: 'success',
        });

        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (e: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (e && e.message) || t('pages.application_challenge.application_challenge_delete_error_toast'),
        });
      },
    });
  };

  const handleCloseChallege = useCallback(() => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalClose(false);

    closeMutation.mutate(selectedItem.uuid as string, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('pages.challenges_details.toasts.close_success'),
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (response: any) => {
        toast({
          status: 'error',
          title: (response && response.message) || t('pages.challenges_details.toasts.close_error'),
        });
      },
    });
  }, [closeMutation, forceRefetch, selectedItem, t, toast]);

  const onHandleEdit = (uuid: string) => {
    navigate(`/application/challenge/${uuid}`);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent
      title={t('pages.application_challenge.application_challenge_page_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Button
          onClick={() => navigate('/application/challenge/create')}
          display='flex'
          alignItems='center'
          gap={2}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.application_challenge.application_challenge_create_button')}
        </Button>
      }
    >
      <Flex direction='column' gap='small'>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
            <Flex alignItems='center' gap={4}>
              {isLoadingAppArea ? (
                <>
                  <Skeleton w='130px' h='21px' />
                  <Skeleton w='102px' h='21px' />
                </>
              ) : (
                <>
                  <Flex direction='column'>
                    <Flex alignItems='center' gap={4}>
                      <Text fontWeight='bold' color='brand.500'>
                        {t('pages.application_challenge.application_challenge_label')}
                      </Text>

                      <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                        {isAppAreaActive
                          ? t('pages.application_challenge.application_challenge_enabled_label')
                          : t('pages.application_challenge.application_challenge_disabled_label')}
                      </Switch>
                    </Flex>

                    <Text fontSize='14px' color='text.secondary' mt={2}>
                      {t('pages.application_challenge.application_challenge_description')}
                      <b> {t('pages.application_challenge.application_challenge_important')}</b>{' '}
                      {t('pages.application_challenge.application_challenge_one')}
                    </Text>
                  </Flex>
                </>
              )}
            </Flex>
          </Flex>

          <SectionBox bodyProps={{ gap: 'small' }}>
            <ChallengeFiltersBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
          </SectionBox>

          <Flex gap={{ base: 4, md: 8 }} direction={{ base: 'column', md: 'row' }}>
            {isLoadingMetrics ? (
              <>
                <Skeleton w='100%' minH={{ base: '121px', md: '174px' }} borderRadius='10px' />
                <Skeleton w='100%' minH={{ base: '121px', md: '174px' }} borderRadius='10px' />
                <Skeleton w='100%' minH={{ base: '121px', md: '174px' }} borderRadius='10px' />
              </>
            ) : (
              <>
                <NumberChartCard
                  w='100%'
                  label={t('pages.application_challenge.application_challenge_total_challenges_label')}
                  value={metrics?.challenges ?? 0}
                />
                <NumberChartCard
                  w='100%'
                  label={t('pages.application_challenge.application_challenge_total_participants_label')}
                  value={metrics?.total_participants ?? 0}
                />
                <NumberChartCard
                  w='100%'
                  label={t('pages.application_challenge.application_challenge_winners_label')}
                  value={metrics?.winners ?? 0}
                />
              </>
            )}
          </Flex>

          <Flex direction='column' gap='deka' mt={8}>
            <Flex direction='column' gap='micro'>
              <ChallengeTable
                data={itemsData}
                isLoading={isLoading}
                sorting={sorting}
                pageNumber={pagination.pageNumber}
                pageSize={pagination.pageSize}
                totalPages={itemData?.meta.pages || 0}
                totalItems={itemData?.meta.total || 0}
                onSortingChange={(field, desc) => setSorting({ field, desc })}
                onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
                onEditClick={item => onHandleEdit(item.uuid)}
                onDeleteClick={item => onHandleDelete(item)}
                onFinishClick={item => onHandleClose(item)}
              />
            </Flex>
          </Flex>
        </SectionBox>
      </Flex>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_challenge.application_challenge_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_challenge.application_challenge_delete_modal_description', {
              title: selectedItem?.title,
            })}
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_challenge.application_challenge_app_area_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_challenge.application_challenge_app_area_modal_description_enable')
              : t('pages.application_challenge.application_challenge_app_area_modal_description_disable')}
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isConfirmModalClose}
        onClose={() => setIsConfirmModalClose(false)}
        onConfirm={handleCloseChallege}
        title='Encerrar desafio'
        description='Tem certeza que deseja encerrar esse desafio? Todas as participações em andamento serão interrompidas.'
        isLoading={closeMutation.isLoading}
      />
      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea || deleteMutation.isLoading || closeMutation.isLoading} />
    </PageContent>
  );
};
